import { Crisp } from "crisp-sdk-web";
import { integrationService } from "@/services/integrationService";
import pkg from "../../package.json";

const DEFAULT_PARTNER = "Posbel";

const ANDROID_ENVIRONMENTS = {
    production: "production",
    blackbox: "production",
    development: "staging",
    "blackbox-staging": "staging",
    local: "debug"
};

let currentToken = null;
let currentSignature = null;
let pendingProfile = null;
let generation = 0;
let sessionHookBound = false;

function isBlackbox() {
    return process.env.VUE_APP_CERTIFIED === "true";
}

function clean(obj) {
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== null && value !== undefined && value !== "")
    );
}

function buildProfile(user, org, { blackbox, rawOrgId, orgId, segmented }) {
    let nickname;
    if (org.orgId) {
        nickname = blackbox ? `${org.name} - BB ${org.orgId}` : `${org.name} - ${org.orgId}`;
    }

    return {
        nickname,
        email: org.contactEmail || user.email,
        phone: org.telephone,
        company: org.name
            ? {
                name: `${org.name} (${rawOrgId})`,
                data: clean({
                    url: org.website,
                    description: org.address
                })
            }
            : undefined,
        segments: Object.values(segmented),
        withIntegrations: user.type === "ADMIN",
        data: {
            ...segmented,
            ...clean({
                orgId,
                accountType: user.type,
                orgName: org.name,
                username: user.username,
                abilities: Array.isArray(user.abilities) ? user.abilities.join(",") : undefined,
                companyVatNumber: org.vatNumber,
                companyEstablishmentNumber: org.establishmentNumber,
                companyPhone: org.telephone,
                companyAddress: org.address,
                companyWebsite: org.website,
                vwMerchantId: org.vwTerminalMerchantId,
                vwMultiMerchantId: user.vwMultiMerchantId,
                paymentIntegration: org.vwEnabled ? "viva-wallet" : undefined,
                posId: blackbox ? org.fdmPosId : undefined,
                vwUserTerminalId: user.vwTerminalId,
                vwOrgTerminalId: org.vwTerminalId,
                platform: "web-admin",
                appVersion: pkg.version,
                userAgent: navigator.userAgent,
                browserLanguage: navigator.language,
                createdAt: user.creationTimestamp ? new Date(user.creationTimestamp).toISOString() : undefined
            }),
            blackbox,
            pinLockEnabled: !!org.pinLockEnabled,
            vwEnabled: !!org.vwEnabled
        }
    };
}

function fetchIntegrations(profile, token, expected) {
    if (!profile.withIntegrations) return;
    integrationService.getIntegration()
        .then((integrations) => {
            if (expected !== generation || currentToken !== token) return;
            if (!Array.isArray(integrations) || !integrations.length) return;
            const types = integrations.map((integration) => integration.type).filter(Boolean).join(",");
            if (types) Crisp.session.setData({ integrations: types });
        })
        .catch(() => {});
}

function applyProfile(profile, token, expected) {
    Crisp.chat.show();
    if (profile.nickname) Crisp.user.setNickname(profile.nickname);
    if (profile.email) Crisp.user.setEmail(profile.email);
    if (profile.phone) Crisp.user.setPhone(profile.phone);
    if (profile.company) Crisp.user.setCompany(profile.company.name, profile.company.data);
    Crisp.session.setSegments(profile.segments, true);
    Crisp.session.setData(profile.data);
    fetchIntegrations(profile, token, expected);
}

function bindSessionHook() {
    if (sessionHookBound) return;
    sessionHookBound = true;
    Crisp.session.onLoaded(() => {
        if (!pendingProfile) return;
        applyProfile(pendingProfile, currentToken, generation);
    });
}

function hideCrisp() {
    pendingProfile = null;
    currentSignature = null;
    generation += 1;
    if (!Crisp.isCrispInjected()) {
        currentToken = null;
        return;
    }
    Crisp.setTokenId(undefined);
    currentToken = null;
    Crisp.session.reset();
    Crisp.chat.hide();
}

export function setupCrisp(user) {
    const websiteId = process.env.VUE_APP_CRISP_WEBSITE_ID;
    if (!websiteId) return;
    if (!user || !user.organization) return hideCrisp();
    if (user.type === "SUPER_USER") return hideCrisp();
    if (user.organization.supportChatCrispEnabled !== true) return hideCrisp();
    if (!user.chatToken) return hideCrisp();

    const org = user.organization;
    const blackbox = isBlackbox();
    const rawOrgId = org.orgId ?? user.orgId;
    const orgId = blackbox ? `blackbox_${rawOrgId}` : `${rawOrgId}`;
    const environment = ANDROID_ENVIRONMENTS[process.env.NODE_ENV] || "debug";
    const partner = (org.partnerSource || "").trim() || DEFAULT_PARTNER;
    const token = user.chatToken;

    const segmented = clean({
        environment,
        buildFlavor: blackbox ? "blackbox" : "standard",
        appMode: "web-admin",
        partner,
        supportTier: org.supportVipEnabled ? "vip" : undefined
    });

    const profile = buildProfile(user, org, { blackbox, rawOrgId, orgId, segmented });
    const signature = `${token}|${JSON.stringify(profile)}`;
    const injected = Crisp.isCrispInjected();

    if (injected && signature === currentSignature) return;

    generation += 1;
    pendingProfile = profile;
    currentSignature = signature;

    if (!injected) {
        Crisp.configure(websiteId, {
            autoload: false,
            tokenId: token,
            locale: (navigator.language || "en").split("-")[0].toLowerCase()
        });
        currentToken = token;
        bindSessionHook();
        Crisp.load();
        applyProfile(profile, token, generation);
        return;
    }

    bindSessionHook();

    if (currentToken !== token) {
        Crisp.setTokenId(token);
        currentToken = token;
        Crisp.session.reset();
        return;
    }

    applyProfile(profile, token, generation);
}

export function resetCrisp() {
    hideCrisp();
}
